"use client";

import { useEffect, useMemo, useRef } from "react";
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { X } from "lucide-react";

const stripePublicKey = import.meta.env.VITE_STRIPE_PUBLIC_KEY || "";
const stripePromise = stripePublicKey ? loadStripe(stripePublicKey) : Promise.resolve(null);

export function StripeCheckoutModal({
  isOpen,
  clientSecret,
  onClose,
  onComplete,
}: {
  isOpen: boolean;
  clientSecret: string | null;
  onClose: () => void;
  onComplete: () => void;
}) {
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const options = useMemo(
    () => ({
      clientSecret,
      onComplete: () => onCompleteRef.current(),
    }),
    [clientSecret],
  );

  if (!isOpen) return null;

  const canRenderCheckout = !!stripePublicKey && !!clientSecret;

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white w-full sm:max-w-2xl sm:rounded-xl rounded-t-2xl max-h-[90vh] overflow-hidden animate-in slide-in-from-bottom sm:slide-in-from-bottom-0 duration-200">
        <div className="flex items-center justify-between px-5 py-4 border-b border-grey-100">
          <div>
            <h3 className="text-lg font-semibold text-grey-900">Card payment</h3>
            <p className="text-sm text-grey-500">Complete your payment securely with Stripe.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-grey-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-grey-500" />
          </button>
        </div>

        <div className="max-h-[calc(90vh-81px)] overflow-y-auto p-4 sm:p-6">
          {canRenderCheckout ? (
            <EmbeddedCheckoutProvider
              key={clientSecret}
              stripe={stripePromise}
              options={options}
            >
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          ) : (
            <div className="rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-700">
              Stripe is not configured for this booking flow. Please contact support.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
